// WEB ROUTES
export const BASE_ROUTES = {
    home: 'home',
} as const;

export const LEGAL_ROUTES = {
    terms: 'terms',
    privacy: 'privacy',
} as const;

export const MARKETING_ROUTES = {
    pricing: 'pricing',
    glossary: 'glossary',
    help: 'help.index',
    contactSales: 'contact-sales.store',
} as const;

export const FEEDBACK_ROUTES = {
    store: 'feedback.store',
} as const;

// Off-platform supplier free-tier surface on the central domain: an unclaimed
// canonical supplier profile can be claimed by its owner.
export const CANONICAL_ROUTES = {
    claimStore: 'canonical.claim.store',
} as const;

export const AUTH_ROUTES = {
    /** Sanctum's endpoint that starts a session and sets the XSRF-TOKEN cookie. */
    csrfCookie: 'sanctum.csrf-cookie',
    login: 'login',
    oauthLogin: 'oauth-login',
    register: 'register',
    registerTenant: 'register-tenant',
    logout: 'logout',
    passwordReset: 'password.reset',
    passwordStore: 'password.store',
    passwordEmail: 'password.email',
    passwordRequest: 'password.request',
    passwordConfirm: 'password.confirm',
    passwordConfirmPasskeyOptions: 'password.confirm.passkey-options',
    passwordConfirmPasskey: 'password.confirm.passkey',
    passwordConfirmCode: 'password.confirm.code',
    passwordConfirmPasswordLink: 'password.confirm.password-link',
    verificationSend: 'verification.send',
    passwordSet: 'password.set',
    passwordSetStore: 'password.set.store',
    oauthConfirmName: 'oauth.confirm-name',
    registerTenantValidateStep: 'register-tenant.validate-step',
    registerTenantScrapeUrl: 'register-tenant.scrape-url',
    supplierSignup: 'supplier-signup.show',
    supplierSignupPassword: 'supplier-signup.store-password',
    supplierSignupOauth: 'supplier-signup.oauth',
    loginPasskeyOptions: 'login.passkey.options',
    loginPasskey: 'login.passkey',
    /** Staging-only QA sign-in with an emailed code. */
    loginQaPinRequest: 'login.qa-pin.request',
    loginQaPin: 'login.qa-pin',
    twoFactorChallenge: 'two-factor.challenge',
    twoFactorChallengePasskeyOptions: 'two-factor.challenge.passkey-options',
    twoFactorChallengePasskey: 'two-factor.challenge.passkey',
    twoFactorSetup: 'two-factor.setup',
    twoFactorSetupPasskeyOptions: 'two-factor.setup.passkey-options',
    twoFactorSetupPasskey: 'two-factor.setup.passkey',
    twoFactorRecoveryCodes: 'two-factor.recovery-codes',
    twoFactorRecoveryCodesAcknowledge: 'two-factor.recovery-codes.acknowledge',
} as const;

export const DASHBOARD_ROUTES = {
    index: 'dashboard.index',
} as const;

export const SETUP_ROUTES = {
    itineraryTemplateStore: 'setup.itinerary.template.store',
    show: 'setup.show',
    advance: 'setup.advance',
    skip: 'setup.skip',
    exit: 'setup.exit',
    reopen: 'setup.reopen',
    complete: 'setup.complete',
    // Task 26: the summary step's single PayFast checkout for a billable total.
    checkout: 'setup.checkout',
    checkoutAbandon: 'setup.checkout.abandon',
    // Middleware/guard-conflict audit HIGH 2: the wizard's OWN "contact
    // sales" inquiry — see routes/tenant/setup.php for why this exists
    // separately from ORGANISATION_ROUTES.contactSales (billing.manage-gated).
    contactSales: 'setup.contact-sales',
    brandsStore: 'setup.brands.store',
    brandsDestroy: 'setup.brands.destroy',
    scrapeUrl: 'setup.scrape-url',
    uploadsAssign: 'setup.uploads.assign',
    rateSheetRetry: 'setup.rate-sheet.retry',
    // Task 21: setup-scoped Tourplan credentials/test-connection — see
    // `EnsureModuleSelectedForSetup`'s docblock for why these exist
    // separately from `INTEGRATIONS_ROUTES.tourplan.*`.
    tourplanCredentials: 'setup.tourplan.credentials',
    tourplanTest: 'setup.tourplan.test',
    // Task 23: setup-scoped Dropbox OAuth kickoff — see the same middleware.
} as const;

export const SETTINGS_ROUTES = {
    index: 'settings.index',
    profile: 'settings.profile',
    profileEdit: 'settings.profile.edit',
    profileUpdate: 'settings.profile.update',
    profileDestroy: 'settings.profile.destroy',
    password: 'settings.password',
    passwordEdit: 'settings.password.edit',
    passwordUpdate: 'settings.password.update',
    notificationsEdit: 'settings.notifications.edit',
    notificationsUpdate: 'settings.notifications.update',
    appearance: 'settings.appearance',
    twoFactorEdit: 'settings.two-factor.edit',
    twoFactorStore: 'settings.two-factor.store',
    twoFactorDestroy: 'settings.two-factor.destroy',
    twoFactorRecoveryCodes: 'settings.two-factor.recovery-codes',
    twoFactorRecoveryCodesAcknowledge: 'settings.two-factor.recovery-codes.acknowledge',
    passkeyOptions: 'settings.two-factor.passkeys.options',
    passkeyStore: 'settings.two-factor.passkeys.store',
    passkeyUpdate: 'settings.two-factor.passkeys.update',
    passkeyDestroy: 'settings.two-factor.passkeys.destroy',
    tourComplete: 'settings.tour.complete',
    tourReset: 'settings.tour.reset',
} as const;

export const GRAPH_EDITOR_ROUTES = {
    index: 'graph-editor.index',
    apiUpdateProperty: 'graph-editor.api.property.update',
    apiUpdateRate: 'graph-editor.api.rate.update',
    apiUpdateServiceProvider: 'graph-editor.api.service-provider.update',
    apiUpdateService: 'graph-editor.api.service.update',
    apiStoreRate: 'graph-editor.api.rate.store',
    apiRollForwardRates: 'graph-editor.api.rates.roll-forward',
    apiArchiveRate: 'graph-editor.api.rate.archive',
    apiRestoreRate: 'graph-editor.api.rate.restore',
    apiStoreServiceCategory: 'graph-editor.api.service-category.store',
    apiStoreService: 'graph-editor.api.service.store',
    apiDeleteProperty: 'graph-editor.api.property.destroy',
    apiDeleteServiceProvider: 'graph-editor.api.service-provider.destroy',
    apiDeleteServiceCategory: 'graph-editor.api.service-category.destroy',
    apiDeleteService: 'graph-editor.api.service.destroy',
    apiDeleteRate: 'graph-editor.api.rate.destroy',
    apiPublishChanges: 'graph-editor.api.changes.publish',
    apiDiscardChanges: 'graph-editor.api.changes.discard',
    apiChangesPending: 'graph-editor.api.changes.pending',
    apiDiscardPendingEntity: 'graph-editor.api.changes.pending.discard',
    apiDiscardPendingField: 'graph-editor.api.changes.pending.discard-field',
    apiChangesHistory: 'graph-editor.api.changes.history',
} as const;

/**
 * Web enrichment. Deliberately NOT nested under the graph-editor or rate
 * routes — the same panel renders on both surfaces, so the endpoints sit in
 * their own group (see routes/tenant/enrichment.php).
 */
export const ENRICHMENT_ROUTES = {
    store: 'enrichment.store',
    show: 'enrichment.show',
    latest: 'enrichment.latest',
    document: 'enrichment.document',
    reconcile: 'enrichment.reconcile',
    consume: 'enrichment.consume',
    unconsume: 'enrichment.unconsume',
    continue: 'enrichment.continue',
} as const;

/**
 * "Fill missing details" for a whole rate sheet: the Guide panel's preview
 * and run cards. Mirrors the `FILL_MISSING_*` names in `App\Constants\RouteName`.
 */
export const FILL_MISSING_ROUTES = {
    start: 'fill-missing.start',
    document: 'fill-missing.document',
    show: 'fill-missing.show',
    cancel: 'fill-missing.cancel',
    continue: 'fill-missing.continue',
    applied: 'fill-missing.applied',
    skipped: 'fill-missing.skipped',
} as const;

export const GUIDE_ROUTES = {
    conversations: {
        index: 'guide.conversations.index',
        store: 'guide.conversations.store',
        show: 'guide.conversations.show',
        destroy: 'guide.conversations.destroy',
        messages: {
            store: 'guide.conversations.messages.store',
        },
    },
    messages: {
        rate: 'guide.messages.rate',
    },
    articles: {
        show: 'guide.articles.show',
    },
    settings: {
        update: 'guide.settings.update',
    },
    plans: {
        report: 'guide.plans.report',
    },
    walkthroughs: {
        report: 'guide.walkthroughs.report',
    },
    feedback: {
        report: 'guide.feedback.report',
    },
    escalations: {
        show: 'guide.escalations.show',
    },
} as const;

export const TEAM_ROUTES = {
    store: 'team.store',
    updateMemberRole: 'team.members.update-role',
    updateMemberPermissions: 'team.members.update-permissions',
    updateMemberProfile: 'team.members.update-profile',
    cancelInvite: 'team.destroy',
    removeMember: 'team.remove-member',
    permissionCatalog: 'team.permissions.catalog',
    transferOwnership: 'team.ownership-transfer.store',
    cancelOwnershipTransfer: 'team.ownership-transfer.cancel',
    acceptOwnershipTransfer: 'team.ownership-transfer.accept',
    declineOwnershipTransfer: 'team.ownership-transfer.decline',
} as const;

export const ORGANISATION_ROUTES = {
    index: 'organisation.index',
    billing: 'organisation.billing',
    invoices: 'organisation.billing.invoices',
    invoiceDownload: 'organisation.billing.invoices.download',
    usage: 'organisation.billing.usage',
    checkoutSuccess: 'organisation.billing.checkout-success',
    checkoutCancel: 'organisation.billing.checkout-cancel',
    verifyCheckout: 'organisation.billing.verify-checkout',
    details: 'organisation.details',
    team: 'organisation.team',
    detailsUpdate: 'organisation.details.update',
    destroy: 'organisation.destroy',
    upgrade: 'organisation.upgrade',
    upgradeQuote: 'organisation.upgrade.quote',
    downgrade: 'organisation.downgrade',
    resumeSubscription: 'organisation.resume-subscription',
    modulesToggle: 'organisation.modules.toggle',
    modulesCheckout: 'organisation.modules.checkout',
    moduleSeatsActivate: 'organisation.modules.seats.activate',
    moduleSeatsDeactivate: 'organisation.modules.seats.deactivate',
    // Organisation → Seats: every per-seat module's seats on one page.
    seats: {
        index: 'organisation.seats.index',
        update: 'organisation.seats.update',
    },
    updateCompany: 'organisation.update.company',
    twoFactorPolicyUpdate: 'organisation.two-factor-policy.update',
    memberTwoFactorReset: 'organisation.members.two-factor.reset',
    logoUpload: 'organisation.upload-logo',
    logoDelete: 'organisation.delete-logo',
    topUpPurchase: 'organisation.top-up.purchase',
    topUpPacks: 'organisation.top-up.packs',
    contactSales: 'organisation.contact-sales',
} as const;

export const BRAND_ROUTES = {
    index: 'organisation.brands.index',
    create: 'organisation.brands.create',
    store: 'organisation.brands.store',
    edit: 'organisation.brands.edit',
    update: 'organisation.brands.update',
    destroy: 'organisation.brands.destroy',
    brandingUpdate: 'organisation.brands.branding.update',
    documentsUpdate: 'organisation.brands.documents.update',
    // Supplier-only active-brand switch; lives outside the organisation prefix.
    contextSwitch: 'brand-context.switch',
} as const;

export const TOKEN_ROUTES = {
    index: 'tokens.index',
    store: 'tokens.store',
    update: 'tokens.update',
    destroy: 'tokens.destroy',
} as const;

export const STO_FORM_ROUTES = {
    agreements: {
        show: 'sto-forms.templates.show',
        pdf: 'sto-forms.templates.pdf',
        edit: 'sto-forms.templates.edit',
        assignFields: 'sto-forms.templates.assign-fields',
        store: 'sto-forms.templates.store',
        update: 'sto-forms.templates.update',
        destroy: 'sto-forms.templates.destroy',
        send: 'sto-forms.templates.send',
        duplicate: 'sto-forms.templates.duplicate',
        terminate: 'sto-forms.templates.terminate',
        signwell: {
            create: 'sto-forms.templates.signwell.create',
            recreate: 'sto-forms.templates.signwell.recreate',
        },
    },
    submissions: {
        show: 'sto-forms.submissions.show',
        showReview: 'sto-forms.submissions.show-review',
        sign: 'sto-forms.submissions.sign',
        save: 'sto-forms.submissions.save',
        submit: 'sto-forms.submissions.submit',
        review: 'sto-forms.submissions.review',
        update: 'sto-forms.submissions.update',
        terminate: 'sto-forms.submissions.terminate',
        download: 'sto-forms.submissions.download',
        destroy: 'sto-forms.submissions.destroy',
    },
    contracts: {
        index: 'sto-forms.contracts.index',
        terminated: 'sto-forms.contracts.terminated',
        expired: 'sto-forms.contracts.expired',
        declined: 'sto-forms.contracts.declined',
        show: 'sto-forms.contracts.show',
        sign: 'sto-forms.contracts.sign',
        save: 'sto-forms.contracts.save',
        submit: 'sto-forms.contracts.submit',
        update: 'sto-forms.contracts.update',
        download: 'sto-forms.contracts.download',
    },
    analytics: {
        contracts: 'sto-forms.analytics.contracts',
    },
    reports: {
        completionRates: 'sto-forms.reports.completion-rates',
        export: 'sto-forms.reports.export',
    },
} as const;

export const RATE_ROUTES = {
    index: 'rates.index',
    create: 'rates.create',
    contextOptions: 'rates.contextOptions',
    sharingOptions: 'rates.sharingOptions',
    store: 'rates.store',
    bulk: 'rates.bulk',
    batchStore: 'rates.batch.store',
    batchStatus: 'rates.batch.status',
    show: 'rates.show',
    review: 'rates.review',
    reviewScope: { update: 'rates.review-scope.update' },
    update: 'rates.update',
    destroy: 'rates.destroy',
    deletionPreview: 'rates.deletionPreview',
    view: 'rates.view',
    download: 'rates.download',
    reprocess: 'rates.reprocess',
    retryFinalization: 'rates.retryFinalization',
    cancel: 'rates.cancel',
    reExtract: 'rates.reExtract',
    apiExtractionProgress: 'rates.api.extractionProgress',
    apiUpdateExtractionDraft: 'rates.api.updateExtractionDraft',
    apiExtractionCostImpact: 'rates.api.extractionCostImpact',
    apiExtractionLinkedRates: 'rates.api.extractionLinkedRates',
    apiUpdateReviewedEntities: 'rates.api.updateReviewedEntities',
    apiUpdateReviewMarks: 'rates.api.updateReviewMarks',
    apiFinalizeExtraction: 'rates.api.finalizeExtraction',
    incomingRequests: 'rates.incoming-requests',
    respondToRequest: 'rates.incoming-requests.respond',
    declineRequest: 'rates.incoming-requests.decline',
    managedSuppliersIndex: 'rates.managed-suppliers.index',
    managedSuppliersStore: 'rates.managed-suppliers.store',
    managedSuppliersShow: 'rates.managed-suppliers.show',
    managedSuppliersUpdate: 'rates.managed-suppliers.update',
    documentSupplierUpdate: 'rates.documents.supplier.update',
    /** Review queue over every sheet awaiting review (`BatchReviewController::all()`). */
    reviewQueue: 'rates.review-queue',
    /** Brand-wide rate-sheet progress for the rates-index banner. */
    progress: 'rates.progress',
    managedDashboard: 'rates.managed-dashboard',
    takeover: 'rates.takeover',
    takeoverConnect: 'rates.takeover.connect',
    /**
     * Lazy per-group document fetch behind the rates index's supplier-grouped
     * table (Phase 3) — `supplierGroupDocuments` takes a managed supplier
     * uuid, `supplierGroupDocumentsUnassigned` has no route parameter and
     * covers the null-`managed_supplier_id` bucket (see
     * `routes/tenant/rates.php`'s comment on why that bucket is a dedicated
     * static route rather than an optional parameter).
     */
    supplierGroupDocuments: 'rates.supplierGroup.documents',
    supplierGroupDocumentsUnassigned: 'rates.supplierGroup.documents.unassigned',
} as const;

/**
 * Bulk rate import (Phase 1): manifest → supplier mapping → presigned
 * direct-to-S3 upload → chunked registration. Deliberately its own agent-only
 * route group in `routes/tenant/rates.php` — see the comment there — rather
 * than nested under `RATE_ROUTES.bulk`/`batchStore`/`batchStatus` above,
 * which remain the legacy single-shot bulk flow until Task 12 retires it.
 */
export const BULK_IMPORT_ROUTES = {
    batchesStore: 'rates.bulk.batches.store',
    mapping: 'rates.bulk.mapping',
    uploadUrls: 'rates.bulk.uploadUrls',
    uploaded: 'rates.bulk.uploaded',
    register: 'rates.bulk.register',
    status: 'rates.bulk.status',
    review: 'rates.bulk.review',
} as const;

/**
 * Setup-scoped mirror of {@link BULK_IMPORT_ROUTES}, for
 * `RatesEngineUploadStep.vue` alone. Same `BulkImportController` actions,
 * reached through `routes/tenant/setup.php`'s dedicated
 * `setup.rates-engine.bulk.*` group instead — that group accepts the guided
 * setup wizard's own captured module selection in place of real per-tenant
 * entitlement (see `EnsureModuleSelectedForSetup`'s docblock), which the live
 * `rates.bulk.*` group above must never do. `batchesStore`/`mapping` are
 * called directly (not via `useBulkUpload`, which only owns the byte-transfer
 * half); `uploadUrls`/`uploaded`/`register`/`status` are passed to
 * `useBulkUpload()`'s `routeNames` override.
 */
export const SETUP_BULK_IMPORT_ROUTES = {
    batchesStore: 'setup.rates-engine.bulk.batches.store',
    mapping: 'setup.rates-engine.bulk.mapping',
    uploadUrls: 'setup.rates-engine.bulk.uploadUrls',
    uploaded: 'setup.rates-engine.bulk.uploaded',
    register: 'setup.rates-engine.bulk.register',
    status: 'setup.rates-engine.bulk.status',
} as const;

/**
 * The rates manager: the agent-side "selling rates" table that sits over
 * managed rates, showing resolved markup/commission per row and driving
 * bulk rule preview/apply. Gated on `module:rates-engine` +
 * `permission:pricing.manage`. `storeRateRule`/`storeCommission` open the
 * rule/commission dialogs from a row's context menu rather than navigating to
 * `PRICING_ROUTES`.
 */
export const RATE_MANAGER_ROUTES = {
    index: 'rates.manager.index',
    rows: 'rates.manager.rows',
    preview: 'rates.manager.preview',
    ruleTargets: 'rates.manager.rule-targets',
    storeRateRule: 'rates.manager.rate-rule.store',
    updateRuleMarkup: 'rates.manager.rule-markup.update',
    storeCommission: 'rates.manager.commission.store',
    bulkPreview: 'rates.manager.bulk.preview',
    bulkApply: 'rates.manager.bulk.apply',
    reset: 'rates.manager.reset',
    /** Undo on a rule edit made from a row: the previous modifier, sent back. */
    restoreRule: 'rates.manager.rule-modifier.restore',
    /** One commission for a ticked selection, at the reach chosen. */
    bulkCommission: 'rates.manager.bulk.commission',
    archive: 'rates.manager.archive',
    restore: 'rates.manager.restore',
} as const;

export const RATE_REQUEST_ROUTES = {
    index: 'rate-requests.index',
    store: 'rate-requests.store',
    storeBulk: 'rate-requests.storeBulk',
} as const;

export const RATE_SHARE_ROUTES = {
    shareAll: 'rate-shares.share-all',
    shareSpecific: 'rate-shares.share-specific',
    revokeAll: 'rate-shares.revoke-all',
    validateRecipients: 'rate-shares.validate-recipients',
    documentShares: 'rate-shares.document-shares',
    documentSummary: 'rate-shares.document-summary',
    revokeSpecific: 'rate-shares.revoke-specific',
} as const;

export const SHARE_RATES_ROUTES = {
    show: 'share-rates.show',
    uploadRateSheet: 'share-rates.upload-rate-sheet',
    cloneRateSheetAsStoTemplate: 'share-rates.sto-template.from-rate-sheet',
    attachFactSheet: 'share-rates.fact-sheet',
    stoConflicts: 'share-rates.sto-conflicts',
    draft: 'share-rates.draft',
    clearDraft: 'share-rates.draft.clear',
    submit: 'share-rates.submit',
} as const;

export const AGENT_RATE_ROUTES = {
    index: 'agent-rates.index',
    pendingCount: 'agent-rates.pending-count',
    supplierChanges: 'agent-rates.supplier-changes',
    accept: 'agent-rates.accept',
    decline: 'agent-rates.decline',
    viewDocument: 'agent-rates.view-document',
    viewByDocument: 'agent-rates.view-by-document',
} as const;

export const RATE_UPLOAD_ROUTES = {
    show: 'rate-upload.show',
    accept: 'rate-upload.accept',
    upload: 'rate-upload.upload',
    shareExisting: 'rate-upload.share-existing',
    decline: 'rate-upload.decline',
    status: 'rate-upload.status',
    review: 'rate-upload.review',
    updateDraft: 'rate-upload.updateDraft',
    finalize: 'rate-upload.finalize',
    reExtract: 'rate-upload.reExtract',
} as const;

export const SHARED_RATE_ROUTES = {
    index: 'shared-documents.index',
} as const;

export const LIBRARY_ROUTES = {
    index: 'shared-library.index',
    show: 'shared-library.show',
    api: {
        contents: 'shared-library.api.contents',
        search: 'shared-library.api.search',
        stats: 'shared-library.api.stats',
        folderTree: 'shared-library.api.folder-tree',
        linkTargets: 'shared-library.api.link-targets',
        entityImages: 'shared-library.api.entity-images',
        previewAsAgent: 'shared-library.api.preview-as-agent',
        folders: {
            store: 'shared-library.api.folders.store',
            update: 'shared-library.api.folders.update',
            move: 'shared-library.api.folders.move',
            links: 'shared-library.api.folders.links.update',
            permissionsUpdate: 'shared-library.api.folders.permissions.update',
            permissionsApply: 'shared-library.api.folders.permissions.apply',
            permissionsApplyPreview: 'shared-library.api.folders.permissions.apply-preview',
            destroy: 'shared-library.api.folders.destroy',
            deletionImpact: 'shared-library.api.folders.deletion-impact',
            downloadZip: 'shared-library.api.folders.download-zip',
            downloadZipRoot: 'shared-library.api.folders.download-zip-root',
        },
        files: {
            store: 'shared-library.api.files.store',
            update: 'shared-library.api.files.update',
            move: 'shared-library.api.files.move',
            links: 'shared-library.api.files.links.update',
            linksAdd: 'shared-library.api.files.links.add',
            linksRemove: 'shared-library.api.files.links.remove',
            permissionsUpdate: 'shared-library.api.files.permissions.update',
            destroy: 'shared-library.api.files.destroy',
            deletionImpact: 'shared-library.api.files.deletion-impact',
            download: 'shared-library.api.files.download',
            preview: 'shared-library.api.files.preview',
            image: 'shared-library.api.files.image',
        },
    },
} as const;

export const SPECIAL_ROUTES = {
    index: 'specials.index',
    feed: 'specials.feed',
    create: 'specials.create',
    store: 'specials.store',
    show: 'specials.show',
    edit: 'specials.edit',
    update: 'specials.update',
    destroy: 'specials.destroy',
    view: 'specials.view',
    share: 'specials.share',
    extendExpiry: 'specials.extendExpiry',
    archive: 'specials.archive',
    activate: 'specials.activate',
    deactivate: 'specials.deactivate',
    resendNotification: 'specials.resendNotification',
    review: 'specials.review',
    updateDraft: 'specials.api.updateDraft',
    approve: 'specials.api.approve',
    extractionProgress: 'specials.api.extractionProgress',
    reExtract: 'specials.reExtract',
    cancel: 'specials.cancel',
} as const;

export const INTEGRATIONS_ROUTES = {
    tourplan: {
        show: 'integrations.tourplan.show',
        credentialsUpdate: 'integrations.tourplan.credentials.update',
        connectionTest: 'integrations.tourplan.connection.test',
        settingsUpdate: 'integrations.tourplan.settings.update',
        mappingRulesUpdate: 'integrations.tourplan.mapping-rules.update',
        mappingRulesSelect: 'integrations.tourplan.mapping-rules.select',
        propertyMap: 'integrations.tourplan.properties.map',
        propertyUnmap: 'integrations.tourplan.properties.unmap',
        documentPropertyMap: 'integrations.tourplan.documents.properties.map',
        documentPropertyUnmap: 'integrations.tourplan.documents.properties.unmap',
        documentPreview: 'integrations.tourplan.documents.preview',
        priceCodeBulk: 'integrations.tourplan.price-code.bulk',
        optionsRefresh: 'integrations.tourplan.options.refresh',
        ratePreviewBulk: 'integrations.tourplan.rates.preview-bulk',
        history: 'integrations.tourplan.history',
        rateSync: 'integrations.tourplan.rates.sync',
    },
    dropbox: {
        show: 'integrations.dropbox.show',
        connect: 'integrations.dropbox.connect',
        disconnect: 'integrations.dropbox.disconnect',
        browse: 'integrations.dropbox.browse',
        import: 'integrations.dropbox.import',
    },
    marketing: {
        show: 'integrations.marketing.show',
        ga4Update: 'integrations.marketing.ga4.update',
        googleAdsUpdate: 'integrations.marketing.google-ads.update',
        metaUpdate: 'integrations.marketing.meta.update',
        posthogUpdate: 'integrations.marketing.posthog.update',
        test: 'integrations.marketing.test',
        disconnect: 'integrations.marketing.disconnect',
    },
} as const;

export const TOURI_ROUTES = {
    index: 'touri.index',
    show: 'touri.show',
    analytics: 'touri.analytics',
    destroy: 'touri.destroy',
    update: 'touri.update',
    api: {
        stream: 'api.touri.stream',
        chat: 'api.touri.chat',
        search: 'api.touri.search',
        suggestions: 'api.touri.suggestions',
        sessions: 'api.touri.sessions',
        session: 'api.touri.session',
        feedback: 'api.touri.feedback',
        feedbackDestroy: 'api.touri.feedback.destroy',
        sessionFeedback: 'api.touri.session.feedback',
        sessionFeedbackDismiss: 'api.touri.session.feedback.dismiss',
    },
} as const;

// Public itinerary share link routes (central, unauthenticated)
export const PUBLIC_ITINERARY_ROUTES = {
    show: 'public-itinerary.show',
    image: 'public-itinerary.image',
    unlock: 'public-itinerary.unlock',
    track: 'public-itinerary.track',
    enquire: 'public-itinerary.enquire',
    pdf: {
        request: 'public-itinerary.pdf.request',
        status: 'public-itinerary.pdf.status',
        download: 'public-itinerary.pdf.download',
    },
} as const;

// Itinerary Builder routes (agent-only, module-gated add-on)
export const ITINERARY_ROUTES = {
    template: {
        index: 'itinerary.template.index',
        store: 'itinerary.template.store',
        edit: 'itinerary.template.edit',
        update: 'itinerary.template.update',
        author: 'itinerary.template.author',
        saveDraft: 'itinerary.template.draft.save',
        discardDraft: 'itinerary.template.draft.discard',
        duplicate: 'itinerary.template.duplicate',
        setDefault: 'itinerary.template.default',
        destroy: 'itinerary.template.destroy',
        imageUpload: 'itinerary.template.image.upload',
        imageDelete: 'itinerary.template.image.delete',
    },
    itineraries: {
        index: 'itinerary.itineraries.index',
        build: 'itinerary.itineraries.build',
        buildFromBooking: 'itinerary.itineraries.build-from-booking',
        buildFromQuote: 'itinerary.itineraries.build-from-quote',
        show: 'itinerary.itineraries.show',
        stats: 'itinerary.itineraries.stats',
        store: 'itinerary.itineraries.store',
        preview: 'itinerary.itineraries.preview',
        /** Draft PDF: the answer carries the status and download URLs for that render. */
        pdfRequest: 'itinerary.itineraries.pdf.request',
        chatLink: 'itinerary.itineraries.chat.link',
        chatApply: 'itinerary.itineraries.chat.apply',
        imageUpload: 'itinerary.itineraries.image.upload',
        imageDelete: 'itinerary.itineraries.image.delete',
        coverUpload: 'itinerary.itineraries.cover.upload',
        libraryImage: 'itinerary.itineraries.library-image',
        supplierLibraryImage: 'itinerary.itineraries.supplier-library-image',
        libraryProperties: 'itinerary.itineraries.library.properties',
        libraryProperty: 'itinerary.itineraries.library.property',
        sharedImageSuppliers: 'itinerary.itineraries.shared-images.suppliers',
        sharedImages: 'itinerary.itineraries.shared-images',
        update: 'itinerary.itineraries.update',
        publish: 'itinerary.itineraries.publish',
        unpublish: 'itinerary.itineraries.unpublish',
        destroy: 'itinerary.itineraries.destroy',
    },
} as const;

// Quote to Book routes (agent-only, module-gated + per-seat add-on). Everything
// here needs the module, a seat and the `quotes.manage` permission; seats are
// assigned on Organisation → Seats (ORGANISATION_ROUTES.seats).
export const QUOTE_ROUTES = {
    index: 'quotes.index',
    create: 'quotes.create',
    store: 'quotes.store',
    show: 'quotes.show',
    edit: 'quotes.edit',
    update: 'quotes.update',
    destroy: 'quotes.destroy',
    // The options of the current version side by side.
    compare: 'quotes.compare',
    // Budget / Mid / Luxury takes on one trip. Draft-only, like every version write.
    options: {
        store: 'quotes.options.store',
        update: 'quotes.options.update',
        recommend: 'quotes.options.recommend',
        reorder: 'quotes.options.reorder',
        destroy: 'quotes.options.destroy',
    },
    versions: {
        store: 'quotes.versions.store',
        exchangeRates: 'quotes.versions.exchange-rates',
        recalculate: 'quotes.versions.recalculate',
        // Freezes the version, renders the PDF and emails it to the client.
        send: 'quotes.versions.send',
        // The client's answer, recorded by the consultant. Legal on a frozen
        // version and refused on a draft or an already-answered one.
        accept: 'quotes.versions.accept',
        decline: 'quotes.versions.decline',
        // Turns the accepted version into a booking (Phase 3). Idempotent: a
        // second call returns the booking that already exists.
        convertToBooking: 'quotes.versions.convert-to-booking',
        pdf: 'quotes.versions.pdf',
        // Re-dispatches the render for a version whose PDF failed. Legal on a
        // frozen version: only the pdf_* columns move.
        retryPdf: 'quotes.versions.retry-pdf',
    },
    lines: {
        // Commits nothing: the resolver preview an agent sees before adding.
        price: 'quotes.lines.price',
        // Every rate the property could sell a stay at, each priced. Commits nothing.
        options: 'quotes.lines.options',
        store: 'quotes.lines.store',
        update: 'quotes.lines.update',
        destroy: 'quotes.lines.destroy',
        reorder: 'quotes.lines.reorder',
        // A table row is every line one stay priced into.
        groupDestroy: 'quotes.lines.groups.destroy',
        // Undo for a removed row, within a few minutes.
        groupRestore: 'quotes.lines.groups.restore',
        groupAdjust: 'quotes.lines.groups.adjust',
        // A row made an optional extra (priced, outside every total), or put back in it.
        groupOptional: 'quotes.lines.groups.optional',
        // Every stay re-priced for the quote's new party.
        reprice: 'quotes.lines.reprice',
    },
    // A stay's levies, park fees and taxes (founder decision 15 Oct 2026):
    // suggestions outside the total until the consultant decides each one.
    // `decide` / `calculate` / `reset` take the row's `group` and the levy's
    // `levy` key; `chargeAll` takes `{ group }` (one stay) or `{ option_id }`.
    // Mirrors `App\Constants\RouteName::QUOTES_LEVY_*`.
    levies: {
        decide: 'quotes.lines.levies.decide',
        calculate: 'quotes.lines.levies.calculate',
        reset: 'quotes.lines.levies.reset',
        chargeAll: 'quotes.levies.charge-all',
    },
    // A per-line price/discount/margin override, and the requote that
    // re-resolves every line against the current rules (e.g. after an FX or
    // rate change). Overrides outside the consultant's role limits go through
    // PRICING_ROUTES.approvals instead of applying immediately (§14).
    lineOverride: 'quotes.lines.override.update',
    lineOverrideClear: 'quotes.lines.override.destroy',
    // A line the Rate Manager has no selling price for: ask everyone who can
    // set one (in-app + email). Changes nothing on the quote.
    lineRequestSellingPrice: 'quotes.lines.request-selling-price',
    // Whole-quote discount %, distinct from a per-line override.
    discount: 'quotes.discount.update',
    requote: 'quotes.requote',
    // The rate-gap worklist. A rates surface that lives in the quoting module:
    // gated on rates.manage, not on a quote seat.
    rateGaps: {
        index: 'quotes.rate-gaps.index',
        resolve: 'quotes.rate-gaps.resolve',
        dismiss: 'quotes.rate-gaps.dismiss',
        bulkDismiss: 'quotes.rate-gaps.bulk-dismiss',
    },
} as const;

// The pricing engine: selling-price rules, commission, per-tenant settings
// (rounding, FX policy), role override limits, the approvals queue for
// overrides outside those limits, and the audit trail. Agent-only,
// module-gated (`module:rates-engine`) and permission-gated
// (`permission:pricing.manage`) — see App\Services\Permission\PermissionRegistry.
export const PRICING_ROUTES = {
    rules: {
        index: 'pricing.rules.index',
        store: 'pricing.rules.store',
        update: 'pricing.rules.update',
        destroy: 'pricing.rules.destroy',
        // Re-point an orphaned rule (its property/rate id vanished) at a new one.
        reassign: 'pricing.rules.reassign',
    },
    commissions: {
        store: 'pricing.commissions.store',
        update: 'pricing.commissions.update',
        destroy: 'pricing.commissions.destroy',
    },
    settings: {
        edit: 'pricing.settings.edit',
        update: 'pricing.settings.update',
        fx: 'pricing.settings.fx.update',
        limits: 'pricing.settings.limits.update',
        defaultCurrency: 'pricing.settings.default-currency.update',
    },
    approvals: {
        index: 'pricing.approvals.index',
        approve: 'pricing.approvals.approve',
        reject: 'pricing.approvals.reject',
    },
    audit: {
        index: 'pricing.audit.index',
    },
} as const;

// Pricing reports: sales, conversion, consultant discounts, supplier/property
// margin, rule performance, exceptions and stale/expired — one screen and CSV
// export per §12/§17, plus the dashboard summary. Same gating as PRICING_ROUTES.
export const REPORT_ROUTES = {
    pricing: {
        show: 'reports.pricing.show',
        csv: 'reports.pricing.csv',
        dashboard: 'reports.pricing.dashboard',
    },
} as const;

// CRM routes (agent-only, part of Quote to Book — D24).
//
// Everything here needs Quote to Book, a consultant or basic seat on it and the
// `crm.manage` permission; seats are assigned on Organisation → Seats.
//
// `create` and `edit` redirect rather than rendering screens of their own: the
// list opens the form in a dialog and the detail page carries the edit form.
// They are named so links to them resolve.
export const CRM_ROUTES = {
    index: 'crm.index',
    contacts: {
        index: 'crm.contacts.index',
        create: 'crm.contacts.create',
        store: 'crm.contacts.store',
        /** JSON: the contact picker's server search. */
        options: 'crm.contacts.options',
        show: 'crm.contacts.show',
        edit: 'crm.contacts.edit',
        update: 'crm.contacts.update',
        // Archive, never delete: leads and quotes point at these rows.
        archive: 'crm.contacts.archive',
        restore: 'crm.contacts.restore',
    },
    agencies: {
        index: 'crm.agencies.index',
        create: 'crm.agencies.create',
        store: 'crm.agencies.store',
        show: 'crm.agencies.show',
        edit: 'crm.agencies.edit',
        update: 'crm.agencies.update',
        archive: 'crm.agencies.archive',
        restore: 'crm.agencies.restore',
    },
    leads: {
        index: 'crm.leads.index',
        board: 'crm.leads.board',
        // The won half of the pipeline: Confirmed, Travelled, Closed.
        afterSale: 'crm.leads.after-sale',
        create: 'crm.leads.create',
        store: 'crm.leads.store',
        show: 'crm.leads.show',
        update: 'crm.leads.update',
        // A move of its own, not a field on `update`: it writes a transition
        // row, closes the previous one and re-derives won/lost.
        stage: 'crm.leads.stage',
        assign: 'crm.leads.assign',
        destroy: 'crm.leads.destroy',
        // Build a quote from this lead. Needs `quotes.manage` AND a Quote to
        // Book seat on top of the CRM's own gates — creating a quote is a
        // paid-seat action wherever the button lives.
        quotes: 'crm.leads.quotes.store',
        activities: {
            store: 'crm.leads.activities.store',
            update: 'crm.leads.activities.update',
            destroy: 'crm.leads.activities.destroy',
            complete: 'crm.leads.activities.complete',
            reopen: 'crm.leads.activities.reopen',
        },
    },
    // Inbox enquiries (itinerary links and inbound email), waiting to become
    // leads. Tenant-schema rows, so route binding alone scopes them. `raw` is
    // gated on `crm.settings.manage`.
    enquiries: {
        index: 'crm.enquiries.index',
        convert: 'crm.enquiries.convert',
        dismiss: 'crm.enquiries.dismiss',
        restore: 'crm.enquiries.restore',
        release: 'crm.enquiries.release',
        raw: 'crm.enquiries.raw',
    },
    // Gated on `crm.settings.manage`: a source's address and field mapping
    // decide what lands in every consultant's inbox.
    leadSources: {
        index: 'crm.lead-sources.index',
        store: 'crm.lead-sources.store',
        show: 'crm.lead-sources.show',
        update: 'crm.lead-sources.update',
        mapping: 'crm.lead-sources.mapping',
        rotate: 'crm.lead-sources.rotate',
        destroy: 'crm.lead-sources.destroy',
        apiKeyIssue: 'crm.lead-sources.api-key.issue',
        apiKeyRevoke: 'crm.lead-sources.api-key.revoke',
    },
    // Gated on `crm.settings.manage`, not `crm.manage`: renaming a stage
    // changes every consultant's board.
    pipeline: {
        index: 'crm.pipeline.index',
        store: 'crm.pipeline.store',
        update: 'crm.pipeline.update',
        archive: 'crm.pipeline.archive',
        restore: 'crm.pipeline.restore',
    },
} as const;

// Phase 3 — Bookings & Client Money. Settings only for now; receipts,
// invoices, the recognition queue and the VAT report join this group in later
// PRs. Every route 404s while the `bookings` feature flag is off (decision B2).
export const FINANCE_ROUTES = {
    settings: {
        edit: 'finance.settings.edit',
        update: 'finance.settings.update',
        recognitionPolicy: 'finance.settings.recognition-policy',
    },
    // Supplier tax defaults (per-line VAT enhancement): the "set up once" screen
    // that records a default output-VAT treatment per supplier.
    supplierTaxProfiles: {
        index: 'finance.supplier-tax-profiles.index',
        store: 'finance.supplier-tax-profiles.store',
        update: 'finance.supplier-tax-profiles.update',
        destroy: 'finance.supplier-tax-profiles.destroy',
    },
    // Client documents (proformas, tax invoices, credit notes) against a
    // booking (PR 5). `store` needs the booking in context; the rest act on a
    // document already brand-scoped by its own route binding. `taxInvoice` is
    // the on-demand tax invoice for a recognised booking (PR 7).
    invoices: {
        store: 'finance.invoices.store',
        taxInvoice: 'finance.invoices.tax-invoice',
        update: 'finance.invoices.update',
        issue: 'finance.invoices.issue',
        // The manual correction of an issued tax invoice (Phase 4, D4.5). The
        // route, the issuer, the posting rule and its HTTP tests all shipped;
        // only this constant and a button were missing, so the whole corrections
        // workstream was unreachable by a human (audit L11 / WF-3).
        creditNote: 'finance.invoices.credit-note',
        void: 'finance.invoices.void',
        send: 'finance.invoices.send',
        pdf: 'finance.invoices.pdf',
    },
    // Client payments (receipts) and their allocations against a booking (PR 6).
    // `store` needs the booking in context; the rest act on a receipt or
    // allocation already brand-scoped by its own route binding.
    receipts: {
        store: 'finance.receipts.store',
        reverse: 'finance.receipts.reverse',
        allocate: 'finance.receipts.allocate',
        unallocate: 'finance.receipts.unallocate',
    },
    // The brand-wide receipts overview.
    dashboard: 'finance.dashboard',
    // The output-VAT basis of one booking's service lines (audit SEC-1 / WF-8).
    // Operations may no longer set it; `show` is the screen the recognition
    // queue's "N lines need a VAT amount" link lands on, reachable by somebody
    // holding `finance.manage` and nothing else.
    bookingTax: {
        show: 'finance.booking-tax.show',
        update: 'finance.booking-tax.update',
    },
    // The recognition review queue (PR 7): one-click confirm / dismiss.
    recognition: {
        index: 'finance.recognition.index',
        confirm: 'finance.recognition.confirm',
        dismiss: 'finance.recognition.dismiss',
        // Undo a dismissal (audit DAT-13). A dismissal posts nothing, so it must
        // not be permanent — without a way back, one mis-click kept a real sale
        // off the books for good.
        requeue: 'finance.recognition.requeue',
    },
    // The VAT report (PR 7): screen and CSV export, read from the ledger.
    vatReport: {
        index: 'finance.vat-report.index',
        csv: 'finance.vat-report.csv',
    },
    // Supplier payment defaults (Phase 4, D4.1): payment terms, bank details and
    // the remittance address, recorded once per supplier. The creditors twin of
    // `supplierTaxProfiles`.
    supplierPayablesProfiles: {
        index: 'finance.supplier-payables-profiles.index',
        store: 'finance.supplier-payables-profiles.store',
        update: 'finance.supplier-payables-profiles.update',
        destroy: 'finance.supplier-payables-profiles.destroy',
    },
    // Supplier invoices — the creditors ledger (Phase 4 PR 3). A draft may be
    // edited; `approve` numbers, freezes and posts it, and `void` reverses that
    // posting.
    supplierInvoices: {
        index: 'finance.supplier-invoices.index',
        store: 'finance.supplier-invoices.store',
        show: 'finance.supplier-invoices.show',
        update: 'finance.supplier-invoices.update',
        approve: 'finance.supplier-invoices.approve',
        void: 'finance.supplier-invoices.void',
    },
    // The blended-VAT reconciliation queue (Phase 4 PR 4, D4.3): the estimate
    // beside the supplier's actual. `confirm` is a person's stamp, never an
    // adjustment.
    supplierVatReconciliation: {
        index: 'finance.supplier-vat-reconciliation.index',
        confirm: 'finance.supplier-vat-reconciliation.confirm',
    },
    // Supplier payments and their allocations to payables (Phase 4 PR 5).
    // `amount_minor` is signed, so a refund FROM a supplier is a negative
    // payment; `reverse` posts the mirror and releases what it had paid, and an
    // allocation posts NOTHING (B13d).
    supplierPayments: {
        index: 'finance.supplier-payments.index',
        store: 'finance.supplier-payments.store',
        reverse: 'finance.supplier-payments.reverse',
        allocate: 'finance.supplier-payments.allocate',
        unallocate: 'finance.supplier-payments.unallocate',
    },
    // Payment runs — approve-then-pay over ONE currency's approved payables
    // (Phase 4 PR 5, D4.4). `pay` records one payment per SUPPLIER and allocates
    // it across their payables in the run, which is what makes `remittances`
    // one advice per supplier.
    paymentRuns: {
        index: 'finance.payment-runs.index',
        store: 'finance.payment-runs.store',
        approve: 'finance.payment-runs.approve',
        pay: 'finance.payment-runs.pay',
        cancel: 'finance.payment-runs.cancel',
        remittances: 'finance.payment-runs.remittances',
        // Emailing the advices the run produced (audit OPS-20 / WF-24). One
        // message per supplier, never a combined one (D4.4).
        remittancesEmail: 'finance.payment-runs.remittances.email',
        remittancesEmailAll: 'finance.payment-runs.remittances.email-all',
    },
    // The per-supplier statement (Phase 4 PR 5). One screen, two representations,
    // one query: `?format=csv` streams the same statements the page renders, as
    // the VAT report does. The response is a LIST, one statement per currency
    // (B12/P7) — never a cross-currency total.
    supplierStatements: {
        index: 'finance.supplier-statements.index',
    },
} as const;

// Phase 3 — Bookings. The operational side (confirming supplier services,
// working the trip); the money side lives under FINANCE_ROUTES. Every route
// 404s while the `bookings` feature flag is off (decision B2). `services.*` is
// the nested service-line surface; `confirm`/`cancel` are lifecycle POSTs that
// fire events, never field edits.
export const BOOKING_ROUTES = {
    index: 'bookings.index',
    show: 'bookings.show',
    update: 'bookings.update',
    confirm: 'bookings.confirm',
    cancel: 'bookings.cancel',
    services: {
        store: 'bookings.services.store',
        update: 'bookings.services.update',
        destroy: 'bookings.services.destroy',
        reorder: 'bookings.services.reorder',
        confirmation: 'bookings.services.confirmation',
        supplierRequest: 'bookings.services.supplier-request',
        voucher: 'bookings.services.voucher',
    },
    travellers: {
        store: 'bookings.travellers.store',
        update: 'bookings.travellers.update',
        destroy: 'bookings.travellers.destroy',
        reorder: 'bookings.travellers.reorder',
        prefill: 'bookings.travellers.prefill',
    },
    // Operational documents (Phase 6). `download` and `retryPdf` are what the
    // documents card on the booking page drives: a rooming list is never
    // emailed, so downloading it is the only way it is ever produced.
    documents: {
        index: 'bookings.documents.index',
        download: 'bookings.documents.download',
        send: 'bookings.documents.send',
        retryPdf: 'bookings.documents.retry-pdf',
    },
    // Booking-wide document generation: one rooming list / voucher pack per
    // booking, as opposed to the per-service documents under `services.*`.
    roomingList: 'bookings.rooming-list',
    voucherPack: 'bookings.voucher-pack',
} as const;

export const NOTIFICATION_ROUTES = {
    index: 'notifications.index',
    unreadCount: 'notifications.unread-count',
    markAsRead: 'notifications.mark-as-read',
    markAllAsRead: 'notifications.mark-all-as-read',
    destroy: 'notifications.destroy',
    clearAll: 'notifications.clear-all',
} as const;

// Network routes (unified contacts/connections/lists) - brand-to-brand
export const NETWORK_ROUTES = {
    browse: 'network.browse',
    show: 'network.show',
    showBrand: 'network.showBrand',
    showProperty: 'network.showProperty',
    showService: 'network.showService',
    connections: 'network.connections',
    connectionsStore: 'network.connections.store',
    connectionsAccept: 'network.connections.accept',
    connectionsDecline: 'network.connections.decline',
    connectionsDestroy: 'network.connections.destroy',
    lists: {
        index: 'network.lists.index',
        create: 'network.lists.create',
        store: 'network.lists.store',
        show: 'network.lists.show',
        edit: 'network.lists.edit',
        update: 'network.lists.update',
        destroy: 'network.lists.destroy',
        addMembers: 'network.lists.add-members',
        removeMembers: 'network.lists.remove-members',
        previewFilters: 'network.lists.preview-filters',
        toggleActive: 'network.lists.toggle-active',
        stoTemplates: 'network.lists.sto-templates',
    },
} as const;

// @deprecated - Use NETWORK_ROUTES instead
export const CONTACTS_ROUTES = {
    directory: {
        index: 'network.browse',
        show: 'network.show',
        showBrand: 'network.showBrand',
    },
    connections: {
        index: 'network.connections',
        store: 'network.connections.store',
        accept: 'network.connections.accept',
        decline: 'network.connections.decline',
        destroy: 'network.connections.destroy',
    },
} as const;

// @deprecated - Use NETWORK_ROUTES.lists instead
export const CONNECTION_LIST_ROUTES = {
    index: 'network.lists.index',
    create: 'network.lists.create',
    store: 'network.lists.store',
    show: 'network.lists.show',
    edit: 'network.lists.edit',
    update: 'network.lists.update',
    destroy: 'network.lists.destroy',
    addMembers: 'network.lists.add-members',
    removeMembers: 'network.lists.remove-members',
    previewFilters: 'network.lists.preview-filters',
    invitePreview: 'network.lists.invite-preview',
    members: {
        resend: 'network.lists.members.resend',
        update: 'network.lists.members.update',
        destroy: 'network.lists.members.destroy',
        exclude: 'network.lists.members.exclude',
        deleteAgent: 'network.lists.members.delete-agent',
    },
    toggleActive: 'network.lists.toggle-active',
    stoTemplates: 'network.lists.sto-templates',
} as const;

export const MESSAGING_ROUTES = {
    index: 'messaging.index',
    unreadCount: 'messaging.unread-count',
    create: 'messaging.create',
    store: 'messaging.store',
    update: 'messaging.update',
    destroy: 'messaging.destroy',
    sendStoESignature: 'messaging.send-sto-form',
    stoTemplates: 'messaging.sto-templates',
    messages: {
        store: 'messaging.messages.store',
        read: 'messaging.messages.read',
    },
    tickets: {
        index: 'messaging.tickets.index',
        create: 'messaging.tickets.create',
        show: 'messaging.tickets.show',
        store: 'messaging.tickets.store',
        update: 'messaging.tickets.update',
        destroy: 'messaging.tickets.destroy',
        resolve: 'messaging.tickets.resolve',
        reopen: 'messaging.tickets.reopen',
        notes: {
            store: 'messaging.tickets.notes.store',
        },
    },
    conversations: {
        tickets: {
            store: 'messaging.conversations.tickets.store',
        },
    },
} as const;

// API ROUTES
export const API_ROUTES = {
    sharedDocuments: {
        index: 'api.shared-documents.index',
        browse: 'api.shared-documents.browse',
        download: 'api.shared-documents.download',
        preview: 'api.shared-documents.preview',
        downloadZip: 'api.shared-documents.download-zip',
        downloadZipRoot: 'api.shared-documents.download-zip-root',
        downloadSharedZip: 'api.shared-documents.download-shared-zip',
    },
} as const;
